import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000ComboBoxBase from '../../u2000Core/components/formControls/combobox/U2000ComboBoxBase';
import U2000InputTextBase from '../../u2000Core/components/formControls/input/U2000InputTextBase';
import U2000Label from '../../u2000Core/components/formControls/label/U2000Label';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../U3207';
import { u3207ShiftMonthYm } from '../utils/u3207MonthUtils';
import { u3207FormatCodeNameLabel } from '../utils/u3207Formatters';

interface FilterOption {
  id: string;
  label: string;
  [key: string]: unknown;
}

interface U3207MonthFilterProps {
  month: string;
  onMonthChange: (month: string) => void;
  providerId: string;
  onProviderChange: (id: string) => void;
  groupId: string;
  onGroupChange: (id: string) => void;
  providers: Array<{ id: string; code: string; name: string }>;
  groups: Array<{ id: string; code: string; name: string }>;
}

function U3207MonthFilter({
  month,
  onMonthChange,
  providerId,
  onProviderChange,
  groupId,
  onGroupChange,
  providers,
  groups,
}: U3207MonthFilterProps) {
  const { t } = useTranslation(NS);

  const providerOptions = useMemo<FilterOption[]>(
    () => providers.map((p) => ({ id: p.id, label: u3207FormatCodeNameLabel(p.code, p.name) })),
    [providers],
  );

  const groupOptions = useMemo<FilterOption[]>(
    () => groups.map((g) => ({ id: g.id, label: u3207FormatCodeNameLabel(g.code, g.name) })),
    [groups],
  );

  const selectedProvider = useMemo(
    () => providerOptions.find((p) => p.id === providerId) ?? null,
    [providerId, providerOptions],
  );

  const selectedGroup = useMemo(
    () => groupOptions.find((g) => g.id === groupId) ?? null,
    [groupId, groupOptions],
  );

  const goPrev = () => {
    const next = u3207ShiftMonthYm(month, -1);
    if (next) onMonthChange(next);
  };

  const goNext = () => {
    const next = u3207ShiftMonthYm(month, 1);
    if (next) onMonthChange(next);
  };

  return (
    <U2000Panel title={t('filter.title')} className="pt-6!">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <U2000Label label={t('filter.month')} htmlFor="u3207-month" />
          <div className="flex items-center gap-2">
            <U2000ActionBtn
              label="‹"
              variant="secondary"
              handleClick={goPrev}
              title={t('filter.prevMonth')}
              className="px-3! py-2!"
            />
            <div className="w-32">
              <U2000InputTextBase
                fieldName="u3207-month"
                value={month}
                readonly
                setValue={() => undefined}
              />
            </div>
            <U2000ActionBtn
              label="›"
              variant="secondary"
              handleClick={goNext}
              title={t('filter.nextMonth')}
              className="px-3! py-2!"
            />
          </div>
        </div>

        <div className="min-w-56">
          <U2000Label label={t('filter.provider')} />
          <U2000ComboBoxBase<FilterOption>
            data={providerOptions}
            keyField="id"
            descField="label"
            value={selectedProvider}
            onChange={(id) => onProviderChange(id ?? '')}
            noneItemLabel={t('filter.all')}
            placeHolder={t('filter.provider')}
          />
        </div>

        <div className="min-w-56">
          <U2000Label label={t('filter.group')} />
          <U2000ComboBoxBase<FilterOption>
            data={groupOptions}
            keyField="id"
            descField="label"
            value={selectedGroup}
            onChange={(id) => onGroupChange(id ?? '')}
            noneItemLabel={t('filter.all')}
            placeHolder={t('filter.group')}
          />
        </div>
      </div>
    </U2000Panel>
  );
}

export default U3207MonthFilter;
